import { useEffect, useRef, useState } from 'react';
import useSWR, { SWRConfig } from 'swr';

import SettingsPanel from './SettingsPanel';
import EmailGrid from './EmailGrid';
import EmailEditor from './EmailEditor';
import EmailPreview from './EmailPreview';
import {
  DEFAULT_LAYOUTS,
  DEFAULT_SETTINGS,
  EMPTY_EMAILS,
  FLOWS,
  normalizeCurrencyCode,
  Emails,
  Flow,
  EmailDraft,
  Layout,
} from './types';
import {
  AnalyzeBrandVoiceResponse,
  ApiError,
  apiFetch,
  apiPost,
  GenerateAllResponse,
  RegenerateResponse,
  MerchantContext,
  SampleProduct,
} from './api';

type SelectedEmail = { flow: Flow; index: number };

function EmailGeneratorContent({ token }: { token: string }) {
  const [step, setStep] = useState<1 | 2>(1);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [emails, setEmails] = useState(EMPTY_EMAILS);
  const [layouts, setLayouts] = useState(DEFAULT_LAYOUTS);
  const [selectedEmail, setSelectedEmail] = useState<SelectedEmail | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [sampleProducts, setSampleProducts] = useState<SampleProduct[]>([]);
  const {
    data: merchant,
    error,
    mutate,
  } = useSWR<MerchantContext, ApiError>('/email-generator/merchant-context', {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const seeded = useRef(false);

  useEffect(() => {
    if (!merchant || seeded.current) return;
    seeded.current = true;

    setSettings((prev) => ({
      ...prev,
      ...(merchant.company && prev.brandName === DEFAULT_SETTINGS.brandName
        ? { brandName: merchant.company }
        : {}),
      ...(merchant.currency && prev.currencyCode === DEFAULT_SETTINGS.currencyCode
        ? { currencyCode: normalizeCurrencyCode(merchant.currency) }
        : {}),
    }));

    apiFetch<SampleProduct[]>('/email-generator/sample-products', token)
      .then(setSampleProducts)
      .catch(() => {});
  }, [merchant, token]);

  const updateLayout = (flow: Flow, index: number, layout: Layout) => {
    setLayouts((prev) => {
      const updatedFlow = prev[flow].map((l, i) => {
        if (i === index) {
          return layout;
        }
        return l;
      });

      return { ...prev, [flow]: updatedFlow };
    });
  };

  const updateEmail = (flow: Flow, index: number, draft: EmailDraft) => {
    setEmails((prev) => {
      const updatedFlow = prev[flow].map((email, i) => {
        if (i === index) {
          return draft;
        }
        return email;
      });

      return { ...prev, [flow]: updatedFlow };
    });
  };

  const promptSettings = {
    brandName: settings.brandName,
    discountCode: settings.discountCode || undefined,
    tone: settings.tone,
    existingEmailCopy: settings.existingEmailCopy || undefined,
  };

  const toDraft = (email: RegenerateResponse): EmailDraft => ({
    subject: email.subject,
    previewText: email.preview,
    headline: email.headline,
    body: email.body,
  });

  const handleGenerateAll = async () => {
    setGenerating(true);
    setGenError(null);

    try {
      const result = await apiPost<GenerateAllResponse>(
        '/email-generator/generate-all',
        token,
        { settings: promptSettings },
      );

      const next: Emails = {
        replenishment: [null, null, null],
        winback: [null, null, null],
        subscription: [null, null, null],
        'cross-sell': [null, null, null],
      };

      result.emails.forEach((email) => {
        if (FLOWS.includes(email.flow) && email.index >= 0 && email.index <= 2) {
          next[email.flow][email.index] = toDraft(email);
        }
      });

      setEmails(next);
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (flow: Flow, index: number) => {
    setGenError(null);
    try {
      const result = await apiPost<RegenerateResponse>(
        '/email-generator/regenerate',
        token,
        { settings: promptSettings, flow, index },
      );

      updateEmail(flow, index, toDraft(result));
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Regeneration failed');
    }
  };

  const handleRegenerateField = async (
    flow: Flow,
    index: number,
    field: 'subject' | 'body',
  ) => {
    setGenError(null);
    try {
      const result = await apiPost<RegenerateResponse>(
        '/email-generator/regenerate',
        token,
        { settings: promptSettings, flow, index },
      );

      const current = emails[flow][index];

      if (current) {
        updateEmail(flow, index, { ...current, [field]: result[field] });
      } else {
        updateEmail(flow, index, toDraft(result));
      }
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Regeneration failed');
    }
  };

  const handleAnalyzeBrandVoice = async (): Promise<string> => {
    const result = await apiPost<AnalyzeBrandVoiceResponse>(
      '/email-generator/analyze-brand-voice',
      token,
      {},
    );

    return result.analysis;
  };

  return (
    <div className={`eg-card${step === 2 && selectedEmail ? ' eg-card--wide' : ''}`}>
      <header className="eg-card-header">
        <h1>Email Generator</h1>
        <p>{merchant?.company ?? 'Merchant settings'}</p>
      </header>

      <div className="eg-stepper" aria-label="Progress">
        <div className={`eg-step ${step === 1 ? 'eg-step--active' : 'eg-step--done'}`}>
          <span className="eg-step-dot">{step === 1 ? '1' : '✓'}</span>
          <span className="eg-step-label">Settings</span>
        </div>
        <div className="eg-step-connector" />
        <div className={`eg-step ${step === 2 ? 'eg-step--active' : ''}`}>
          <span className="eg-step-dot">2</span>
          <span className="eg-step-label">Emails</span>
        </div>
      </div>

      {error ? (
        <div className="eg-alert" role="alert">
          <div>{`Unable to load merchant defaults${error.status ? ` (${error.status} ${error.statusText})` : ''}.`}</div>
          <button type="button" className="eg-alert-action" onClick={() => mutate()}>
            Retry
          </button>
        </div>
      ) : null}
      {genError ? (
        <div className="eg-alert" role="alert">
          <div>{genError}</div>
          <button type="button" className="eg-alert-action" onClick={() => setGenError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      {step === 1 ? (
        <>
          <SettingsPanel
            settings={settings}
            onUpdate={setSettings}
            onAnalyzeBrandVoice={handleAnalyzeBrandVoice}
          />
          <div className="eg-step-actions">
            <button type="button" className="eg-btn-primary" onClick={() => setStep(2)}>
              Continue
            </button>
          </div>
        </>
      ) : null}

      {step === 2 && selectedEmail ? (
        <div className="eg-editor-layout">
          <div className="eg-editor-pane">
            <EmailEditor
              flow={selectedEmail.flow}
              index={selectedEmail.index}
              draft={emails[selectedEmail.flow][selectedEmail.index]}
              layout={layouts[selectedEmail.flow][selectedEmail.index]}
              settings={settings}
              onChange={(draft) => updateEmail(selectedEmail.flow, selectedEmail.index, draft)}
              onLayoutChange={(layout) => updateLayout(selectedEmail.flow, selectedEmail.index, layout)}
              onSettingsChange={setSettings}
              onClose={() => setSelectedEmail(null)}
              onRegenerate={() => handleRegenerate(selectedEmail.flow, selectedEmail.index)}
              onRegenerateField={(field) => handleRegenerateField(selectedEmail.flow, selectedEmail.index, field)}
            />
          </div>
          <EmailPreview
            flow={selectedEmail.flow}
            draft={emails[selectedEmail.flow][selectedEmail.index]}
            layout={layouts[selectedEmail.flow][selectedEmail.index]}
            settings={settings}
            sampleProducts={sampleProducts}
          />
        </div>
      ) : null}

      {step === 2 && !selectedEmail ? (
        <>
          <button type="button" className="eg-back-btn" onClick={() => setStep(1)}>
            ← Back to settings
          </button>
          <EmailGrid
            emails={emails}
            layouts={layouts}
            settings={settings}
            onSelectEmail={(flow, index) => setSelectedEmail({ flow, index })}
            onLayoutChange={updateLayout}
            onGenerateAll={handleGenerateAll}
            generating={generating}
          />
        </>
      ) : null}
    </div>
  );
}

function EmailGenerator({ token }: { token: string }) {
  return (
    <SWRConfig
      value={{
        fetcher: (path: string) => apiFetch<MerchantContext>(path, token),
      }}
    >
      <EmailGeneratorContent token={token} />
    </SWRConfig>
  );
}

export default EmailGenerator;
